import React from 'react';
import { X, Plus, Phone, Mail, MapPin, Heart, User, Package, RefreshCw, MessageCircle, Lock, BarChart3, Box, AlertCircle, Info, ExternalLink } from 'lucide-react';
import { CategoryType, MenuItem } from '../types';

interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginClick: () => void;
  onCategoryClick: (category: string) => void;
  onTrackOrderClick: () => void;
  categories: CategoryType[];
  menuItems: MenuItem[];
  isAdmin: boolean;
  onAdminClick: (tab: 'dashboard' | 'products' | 'orders' | 'reports') => void;
}

// Helper to map string icon names to Lucide components
const IconMap: Record<string, any> = {
    'Plus': Plus,
    'Phone': Phone,
    'Mail': Mail,
    'MapPin': MapPin,
    'Heart': Heart,
    'User': User,
    'Package': Package,
    'RefreshCw': RefreshCw,
    'MessageCircle': MessageCircle,
    'Lock': Lock,
    'BarChart3': BarChart3,
    'Box': Box,
    'AlertCircle': AlertCircle,
    'Info': Info,
    'ExternalLink': ExternalLink
};

export const MenuDrawer: React.FC<MenuDrawerProps> = ({ 
    isOpen, onClose, onLoginClick, onCategoryClick, onTrackOrderClick, categories, menuItems, isAdmin, onAdminClick 
}) => {
  if (!isOpen) return null;

  const handleItemClick = (item: MenuItem) => {
      onClose();
      if (item.type === 'category') {
          onCategoryClick(item.value);
      } else if (item.type === 'action') {
          if (item.value === 'login') onLoginClick();
          if (item.value === 'track_order') onTrackOrderClick();
          // Add other actions if needed
      } else if (item.type === 'link') {
          window.open(item.value, '_blank');
      }
  };

  return (
    <div className="fixed inset-0 z-[100] flex">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer Content */}
      <div className="relative w-[85%] max-w-sm bg-white h-full shadow-xl flex flex-col animate-slide-in-left">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <span className="font-bold text-sm tracking-widest text-gray-800 uppercase">MENU</span>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-800">
            <X size={28} strokeWidth={1} />
          </button>
        </div>

        {/* Menu List */}
        <div className="flex-1 overflow-y-auto">
          <ul className="divide-y divide-gray-100 text-[15px] font-normal text-gray-700">
             
             {/* Admin Shortcuts - Only visible if Admin */}
             {isAdmin && (
                 <li className="bg-gray-50 border-b-4 border-gray-100">
                     <div className="px-5 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Admin Tools</div>
                     <button onClick={() => { onAdminClick('reports'); onClose(); }} className="w-full flex items-center px-5 py-3 hover:bg-gray-100 text-blue-600 font-bold gap-3">
                         <BarChart3 size={18} /> Sales Reports
                     </button>
                     <button onClick={() => { onAdminClick('products'); onClose(); }} className="w-full flex items-center px-5 py-3 hover:bg-gray-100 text-blue-600 font-bold gap-3">
                         <Box size={18} /> Manage Stock / Add Product
                     </button>
                 </li>
             )}

             {/* Dynamic Menu Items */}
             {menuItems.map((item) => {
                 const Icon = item.icon ? IconMap[item.icon] : null;
                 return (
                     <li key={item.id}>
                       <button 
                         onClick={() => handleItemClick(item)}
                         className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
                       >
                         <span className="flex items-center gap-3">
                            {Icon && <Icon size={18} className="text-gray-400" />}
                            {item.label}
                            {item.tag && (
                                <span className={`${item.tagColor || 'bg-red-600'} text-white text-[10px] px-2 py-0.5 rounded-full font-bold`}>
                                    {item.tag}
                                </span>
                            )}
                         </span>
                       </button>
                     </li>
                 );
             })}
          </ul>

          {/* Help Section */}
          <div className="p-6 mt-4 border-t border-gray-100">
            <h4 className="font-medium text-gray-900 mb-4">Need help?</h4>
            <div className="space-y-3">
              <a href="tel:+923180508608" className="flex items-center gap-3 text-gray-500 text-sm hover:text-blue-600">
                <Phone size={16} />
                <span>+92 318 0508608</span>
              </a>
              <a href="mailto:cs@shoebhai.pk" className="flex items-center gap-3 text-gray-500 text-sm hover:text-blue-600">
                <Mail size={16} />
                <span>cs@shoebhai.pk</span>
              </a>
               <a href="tel:03111222393" className="flex items-center gap-3 text-gray-500 text-sm hover:text-blue-600">
                <MessageCircle size={16} />
                <span>031 112 22393</span>
              </a>
            </div>
          </div>
          
          <div className="h-10"></div>
        </div>
      </div>
    </div>
  );
};